import { z } from 'zod';
import { dataSchema, idSchema } from '../common';

/**
 * Verbale di fase: l'operatore fornisce data e testo, il resto (intestazioni,
 * cliente, progetto) lo deriva il sistema. Il PDF non è un'entità propria — nasce
 * come file dentro la fase, quindi qui non c'è uno schema "verbale" salvato,
 * solo l'input della generazione e lo stato del lavoro in coda.
 */

export const verbaleGeneraSchema = z.object({
  /** Data che compare sul documento: non è quella di generazione. */
  data: dataSchema,
  /**
   * Sede in cui si è svolta l'attività verbalizzata, scelta tra quelle coinvolte nel
   * progetto. `null` significa la sede legale del cliente, che non è una riga
   * fra le sedi operative ma un dato dell'anagrafica.
   */
  sedeOperativaId: idSchema.nullable().default(null),
  /**
   * Chi era presente per Evolfin. Si passano gli id e i nomi li risolve chi
   * stampa: un nome copiato nel payload invecchierebbe alla prima correzione
   * dell'anagrafica.
   */
  operatoriIds: z.array(idSchema).min(1, 'Indica almeno una persona presente per Evolfin'),
  /**
   * Chi era presente per l'azienda cliente: **solo stringhe**. La rubrica del
   * cliente serve a proporre i nomi, ma a un incontro può presentarsi
   * qualcuno che in rubrica non c'è — e un verbale deve poter dire chi c'era
   * davvero, non chi era previsto.
   */
  personaleAzienda: z
    .array(z.string().trim().min(1).max(120))
    .max(30, 'Non puoi elencare più di 30 persone')
    .default([]),
  testo: z
    .string()
    .trim()
    .min(1, 'Scrivi i rilevamenti e le attività eseguiti')
    .max(20_000, 'Il testo del verbale non può superare i 20.000 caratteri'),
  /**
   * Nome del file, facoltativo: senza, il sistema usa "Verbale di fase" e, dal
   * secondo in poi, lo stesso nome con la data-ora di generazione.
   */
  nomeFile: z.string().trim().max(120).optional(),
});
export type VerbaleGenera = z.infer<typeof verbaleGeneraSchema>;

/**
 * Stato della generazione, letto dalla coda (nessuna tabella): `null` quando
 * non c'è nessun lavoro in corso sulla fase, che è il caso normale.
 */
export const StatoVerbale = z.enum(['in-coda', 'in-corso', 'fallito']);
export type StatoVerbale = z.infer<typeof StatoVerbale>;

export const verbaleStatoSchema = z.object({
  stato: StatoVerbale.nullable(),
});
export type VerbaleStato = z.infer<typeof verbaleStatoSchema>;
